import { useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appConfig, type EnabledOAuthProvider } from 'shared';
import { useAuthStore } from '~/modules/auth/auth-store';
import type { AuthStep } from '~/modules/auth/types';
import { invitationResumePath } from '~/modules/auth/use-post-auth-redirect';
import { toaster } from '~/modules/common/toaster/toaster';
import { Button } from '~/modules/ui/button';
import { useUIStore } from '~/modules/ui/ui-store';

export const mapOAuthProviders = [
  { id: 'github', name: 'Github' },
  { id: 'google', name: 'Google' },
  { id: 'microsoft', name: 'Microsoft' },
] as const;

type OAuthProvider = (typeof mapOAuthProviders)[number];

/**
 * Display OAuth providers to sign in, sign up, accept invitation
 *
 * @param authStep The action type to perform
 */
export function OAuthProviders({ authStep = 'signIn' }: { authStep: AuthStep }) {
  const { t } = useTranslation();
  const mode = useUIStore((state) => state.mode);
  const { tokenId, redirect } = useSearch({ from: '/_public/auth/authenticate' });
  const inviteOtherAccount = useAuthStore((state) => state.inviteOtherAccount);

  const [loadingProvider, setLoadingProvider] = useState<EnabledOAuthProvider | null>(null);

  const actionText = authStep === 'signIn' ? t('c:sign_in') : authStep === 'signUp' ? t('c:sign_up') : t('c:continue');

  const authenticateWithProvider = async (provider: EnabledOAuthProvider) => {
    try {
      setLoadingProvider(provider);

      const baseUrl = `${appConfig.backendAuthUrl}/${provider}`;
      const params = new URLSearchParams();

      // Only forward an explicit deep link: a default reads as explicit server-side and skips the welcome page for new users.
      if (redirect?.startsWith('/')) params.set('redirectAfter', redirect);

      if (tokenId && inviteOtherAccount) {
        // Another account than the invited address: a plain sign-in that returns to confirm the invitation.
        // The invite flow would refuse it, since it requires the provider address to match the invited one.
        params.set('type', 'auth');
        params.set('redirectAfter', invitationResumePath(tokenId));
      } else if (tokenId) {
        params.set('tokenId', tokenId);
        params.set('type', 'invite');
      } else params.set('type', 'auth');

      const providerUrl = `${baseUrl}?${params.toString()}`;
      window.location.assign(providerUrl);
    } catch (error) {
      toaster.error(t('c:url_malformed'));
      setLoadingProvider(null);
    }
  };

  if (appConfig.enabledOAuthProviders.length < 1) return null;

  return (
    <div data-mode={mode} className="group flex flex-col space-y-2">
      {appConfig.enabledOAuthProviders.map((provider) => {
        const providerData = mapOAuthProviders.find(
          (p): p is OAuthProvider & { id: typeof provider } => p.id === provider,
        );

        if (!providerData) return null;

        return (
          <Button
            loading={loadingProvider === provider}
            key={provider}
            type="button"
            variant="plain"
            className="gap-1"
            onClick={() => authenticateWithProvider(providerData.id)}
          >
            <img
              data-provider={provider}
              src={`/static/auth/${provider}-icon.svg`}
              alt={provider}
              className="mr-1 size-4 data-[provider=github]:group-data-[mode=dark]:invert"
              loading="lazy"
            />
            <span>
              {actionText} {t('c:with').toLowerCase()} {providerData.name}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
